import { User } from "../providers/users"
import { useUsers } from "../providers/users"
import { useAuth } from "../providers/auth"
import { useTranslationFiles } from "../providers/translationFiles"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  TextField
} from "@mui/material"
import FormControl from '@mui/material/FormControl'
import { LoadingButton } from "@mui/lab"
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  NoPhotographyRounded,
  EditRounded,
  CloseRounded,
  EmailRounded
} from "@mui/icons-material"
import {
  ReactNode,
  useEffect,
  useReducer,
  useState
} from "react"
import { PropertyArea, MultiplePropertiesArea } from "./propertyArea"
import { makeStyles } from "@mui/styles"
import {
  useTheme,
  styles,
  stylesDark,
  Styles
} from "../providers/theme"
import { httpsCallable } from 'firebase/functions'
import { socket, functions } from "../index"
import PhoneInput from 'react-phone-number-input'

const useStylesLight = makeStyles<any, Styles>({...styles})
const useStylesDark = makeStyles<any, Styles>({...stylesDark})

interface DialogState {
  dialogActionButtonLabel: string;
  title: string;
  description: string;
  property: string;
  inputType: string;
  inputLabel: string;
  inputRegex: string;
  saveButtonLoading: boolean;
  socketSignal: string;
  separator: string;
  itemsList: string[];
  itemValue: any;
  itemIsValid: boolean;
}

export default function UserCard({
  distributors,
  manufacturersList
}: {
  distributors: string[],
  manufacturersList: string[]
}) {
  const { selectedUser, allFilteredUsers, setSelectedUser } = useUsers()
  const {speechTranslationFilesFW2, webInterfaceTranslationFilesFW2, speechTranslationFilesFW3, webInterfaceTranslationFilesFW3, IBTGTranslationFiles} = useTranslationFiles()
  const { currentRole, user: currentUser } = useAuth()
  const {mode} = useTheme()
  const classesLight = useStylesLight(styles)
  const classesDark = useStylesDark(stylesDark)
  const classes = mode === 'light' ? classesLight : classesDark

  const [dialogState, dispatchDialogState] = useReducer((currentState: DialogState, newState: Partial<DialogState>) => ({
    ...currentState,
    ...newState
  }), {
    dialogActionButtonLabel: '',
    title: '',
    description: '',
    property: '',
    inputType: '',
    inputLabel: '',
    inputRegex: '',
    saveButtonLoading: false,
    socketSignal: '',
    separator: '',
    itemsList: [],
    itemValue: '',
    itemIsValid: true
  })
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [dialogInput, setDialogInput] = useState<ReactNode | null>(<></>)
  const [distributorsListPreload, setDistributorsListPreload] = useState<ReactNode[]>([])
  const [speechTranslationFilesPreload, setSpeechTranslationFilesPreload] = useState<ReactNode[]>([])
  const [webInterfaceTranslationFilesPreload, setWebInterfaceTranslationFilesPreload] = useState<ReactNode[]>([])

  let rolesList = ["None", "Blocked", "User", "Translator", "Remote Support", "Shop", "Administrator", "Global Administrator"]

  const getWrappedIndex = (list: User[], index: number) => {
    const len = list.length;
    if (len === 0) {
      return null;
    }
    const wrappedIndex = ((index % len) + len) % len;
    return wrappedIndex;
  }

  const prevUser = () => {
    const index = allFilteredUsers.findIndex(u => u.id === selectedUser?.id)
    setSelectedUser(allFilteredUsers.at(getWrappedIndex(allFilteredUsers, (index ?? 0) - 1) ?? 0) ?? null);
  }

  const nextUser = () => {
    const index = allFilteredUsers.findIndex(u => u.id === selectedUser?.id)
    setSelectedUser(allFilteredUsers.at(getWrappedIndex(allFilteredUsers, (index ?? 0) + 1) ?? 0) ?? null);
  }

  useEffect(() => {
    const inputType = dialogState.inputType.toLowerCase()
    if (inputType.includes('select')) {
      setDialogInput(
        <FormControl sx={{ width: "100%", mt: 3, mb: 3 }}>
          <Select
            multiple={dialogState.inputType.toLowerCase().includes('multiple')}
            value={dialogState.inputType.toLowerCase().includes('multiple') ? dialogState.itemValue !== "" ? dialogState.itemValue.split(dialogState.separator) : [] : dialogState.itemValue}
            displayEmpty
            renderValue={(selected) => {
                if (selected.length === 0) {
                  return "None"
                }
                return selected.join(', ')
            }}
            onChange={(event) => {
              const { target: { value } } = event
              let newValue = ''
              if (typeof value !== 'string') {
                // [S.S.] Multiple roles have been selected.
                if (value[value.length - 1] === "None") {
                  newValue = "None"
                } else if (value[value.length - 1] === "Blocked") {
                  newValue = "Blocked"
                } else if (value[value.length - 1] === "Global Administrator") {
                  newValue = "Global Administrator"
                } else {
                  let index = value.indexOf("None") 
                  if (index !== -1)  {
                    value.splice(index, 1)
                  }
                  index = value.indexOf("Blocked") 
                  if (index !== -1)  {
                    value.splice(index, 1)
                  }
                  index = value.indexOf("Global Administrator") 
                  if (index !== -1)  {
                    value.splice(index, 1)
                  }
                  newValue = value.join(dialogState.separator)
                }
                if (newValue === "") {
                  newValue = "None"
                }
              } else {
                newValue = value
              }
              dispatchDialogState({itemValue: newValue})
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: mode === 'light' ? "#fff" : "#151515",
                  borderRadius: '10px',
                  '&::-webkit-scrollbar': {
                    width: ".3em",
                  },
                  '&::-webkit-scrollbar-track': {
                    background: "transparent",
                    boxShadow: "inset 0 0 10px #1c1c1c",
                    borderRadius: "5px",
                    borderLeft: "1px solid transparent",
                    borderRight: "1px solid transparent",
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#444',
                    outline: '1px solid #444',
                    borderRadius: ".15em"
                  }
                }
              }
            }}
            input={<OutlinedInput />}
          >
            {
              dialogState.itemsList !== undefined ?
                dialogState.property === 'Companies' ?
                  distributorsListPreload
                : dialogState.property === 'Speech' ?
                  speechTranslationFilesPreload
                : dialogState.property === 'WebInterface' ?
                  webInterfaceTranslationFilesPreload
                :
                  dialogState.itemsList.map((item: string) => (
                    <MenuItem
                      key={item}
                      value={item}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "rgba(133, 210, 1, 0.3)",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(133, 210, 1, 0.3)",
                        },
                        "&:hover": {
                          backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
                        }
                      }}
                    >
                      {item}
                    </MenuItem>
                  ))
              : null
            }
          </Select>
        </FormControl>
      )
    } else if (inputType === 'textfield') {
      setDialogInput(
        <div
          style={{
            width: "100%",
            marginTop: "1em",
            marginBottom: "1em",
            display: "flex",
            flexDirection: "column"
          }}
        >
          {
            dialogState.itemIsValid ||
              <p style={{ color: "#f53636", margin: 0 }}>
                Invalid list!
              </p>
          }
          <TextField
            sx={{ marginTop: "1em" }}
            label={dialogState.inputLabel}
            value={dialogState.itemValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const newItemValue = event.target.value
              if (dialogState.inputRegex !== "") {
                let regex = new RegExp(dialogState.inputRegex);
                if (newItemValue.match(regex) || newItemValue === "") {
                  dispatchDialogState({itemIsValid: true})
                } else {
                  dispatchDialogState({itemIsValid: false})
                }
              }
              dispatchDialogState({itemValue: newItemValue})
            }}
          />
        </div>
      )
    } else if (inputType === 'phone') {
      setDialogInput(
        <div
          style={{
            marginTop: '1.5em',
            display: 'flex',
            gap: '1em'
          }}
        >
          <PhoneInput
            placeholder={dialogState.inputLabel}
            value={dialogState.itemValue}
            onChange={(newValue: string | undefined) => {
              dispatchDialogState({itemValue: newValue === undefined ? "" : newValue})
            }}
          />
        </div>
      )
    } else if (inputType === 'none') {
      setDialogInput(null)
    }
    // eslint-disable-next-line
  }, [dialogState.itemValue, dialogState.itemsList, dialogState.inputType])

  useEffect(() => {
    setDistributorsListPreload(distributors.map((item: string) => (
      <MenuItem
        key={item}
        value={item}
        sx={{
          "&.Mui-selected": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&:hover": {
            backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
          }
        }}
      >
        {item}
      </MenuItem>
    )))
  }, [distributors, mode])

  useEffect(() => {
    setSpeechTranslationFilesPreload([...new Set([...speechTranslationFilesFW2, ...speechTranslationFilesFW3])].sort().map((item: string) => (
      <MenuItem
        key={item}
        value={item}
        sx={{
          "&.Mui-selected": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(133, 210, 1, 0.3)",
          },
          "&:hover": {
            backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
          }
        }}
      >
        {item}
      </MenuItem>
    )))
  }, [speechTranslationFilesFW2, speechTranslationFilesFW3, mode])

  useEffect(() => {
    setWebInterfaceTranslationFilesPreload([...new Set([...webInterfaceTranslationFilesFW2, ...webInterfaceTranslationFilesFW3])].sort().map((item: string) => (
      <MenuItem
        key={item}
        value={item}
        sx={{
            "&.Mui-selected": {
              backgroundColor: "rgba(133, 210, 1, 0.3)",
            },
            "&.Mui-selected:hover": {
              backgroundColor: "rgba(133, 210, 1, 0.3)",
            },
            "&:hover": {
              backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
            }
        }}
      >
        {item}
      </MenuItem>
    )))
  }, [webInterfaceTranslationFilesFW2, webInterfaceTranslationFilesFW3, mode])

  return (
    <>
      <Modal
        open={selectedUser !== null}
        onClose={() => {
          setSelectedUser(null)
        }}
      >
        <Box
          className={classes.modalContainer}
          sx={{
            flexDirection: "column",
            padding: "2.5em 1.025em 1.5em 1.5em !important"
          }}
        >
          <Box
            className={classes.modalScrollableContainer}
            sx={{
              flexDirection: "column",
              padding: ".5em",
              margin: ""
            }}
          >
            <IconButton
              className={classes.modalButtonClose}
              onClick={() => {
                setSelectedUser(null)
              }}
            >
              <CloseRounded />
            </IconButton>
            <Box sx={{ width: "100%", height: "auto", margin: "0", display: "flex", justifyContent: "center" }}>
              <Box className={classes.userPhotoContainer}>
                {
                  selectedUser!.photoURL ?
                    <img
                      referrerPolicy="no-referrer"
                      className={classes.userPhoto}
                      src={selectedUser!.photoURL}
                      alt={selectedUser!.displayName !== '' ? selectedUser!.displayName : "Unknown"}
                    />
                  :
                    <NoPhotographyRounded fontSize="large" />
                }
              </Box>
            </Box>
            <PropertyArea
              property={"Name"}
              content={`${selectedUser!.displayName || "Unknown"}`}
              classes={{
                container: classes['propertyArea'],
                property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
              }}
              actionButton={true}
              actionButtonsList={[
                {
                  tooltip: "Edit name",
                  disabled: (selectedUser!.role.includes("Administrator") && !(currentRole === "Global Administrator")) || selectedUser!.role === "Global Administrator",
                  onClick: () => {
                    dispatchDialogState({
                      dialogActionButtonLabel: "Save",
                      title: "Edit name field",
                      description: "Insert a name to be displayed for this user.",
                      property: "Name",
                      inputType: "textField",
                      inputLabel: "Display Name",
                      inputRegex: "",
                      saveButtonLoading: false,
                      socketSignal: 'set_display_name',
                      itemValue: selectedUser!.displayName,
                      itemIsValid: true
                    })
                    setDialogOpen(true)
                  },
                  icon: <EditRounded />
                }
              ]}
            />
            <PropertyArea
              property={"Email"}
              content={`${selectedUser!.email}`}
              classes={{
                container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
              }}
              actionButton={true}
              actionButtonsList={[
                {
                  tooltip: "Send e-mail",
                  disabled: false,
                  onClick: () => {
                    window.open(`mailto:${selectedUser!.email}?subject=Index%20Braille%20Distributor%20website%20-%20Update%20User%20Account`)
                  },
                  icon: <EmailRounded />
                }
              ]}
            />
            <PropertyArea
              property={"Phone"}
              content={`${selectedUser!.phoneNumber || "Unknown"}`}
              classes={{
                container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
              }}
              actionButton={true}
              actionButtonsList={[
                {
                  tooltip: "Edit phone",
                  disabled: (selectedUser!.role.includes("Administrator") && !(currentRole === "Global Administrator")) || selectedUser!.role === "Global Administrator",
                  onClick: () => {
                    dispatchDialogState({
                      dialogActionButtonLabel: "Save",
                      title: "Edit phone field",
                      description: "Pick a country and insert a phone number to be displayed for this user.",
                      property: "Phone",
                      inputType: "phone",
                      inputLabel: "Phone Number",
                      inputRegex: "",
                      saveButtonLoading: false,
                      socketSignal: 'set_phone_number',
                      itemValue: selectedUser!.phoneNumber,
                      itemIsValid: true
                    })
                    setDialogOpen(true)
                  },
                  icon: <EditRounded />
                }
              ]}
            />
            <PropertyArea
              property={"Company"}
              content={selectedUser!.company ? selectedUser!.company : "None"}
              classes={{
                container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
              }}
              actionButton={true}
              actionButtonsList={[
                {
                  tooltip: "Edit company",
                  disabled: (selectedUser!.role.includes("Administrator") && !(currentRole === "Global Administrator")) || selectedUser!.role === "Global Administrator",
                  onClick: () => {
                    dispatchDialogState({
                      dialogActionButtonLabel: "Save",
                      title: "Edit company field",
                      description: "Insert a company to be displayed for this user.",
                      property: "Company",
                      inputType: "textField",
                      inputLabel: "Company",
                      inputRegex: "",
                      saveButtonLoading: false,
                      socketSignal: 'set_company',
                      itemValue: selectedUser!.company,
                      itemIsValid: true
                    })
                    setDialogOpen(true)
                  },
                  icon: <EditRounded />
                }
              ]}
            />
            {
              (currentRole === "Global Administrator" && selectedUser!.role.includes("Remote Support")) &&
                <MultiplePropertiesArea
                  topMargin={true}
                  property={"Remote Support"}
                  classes={classes}
                  children={
                    <>
                      <PropertyArea
                        key={0}
                        property={"Companies"}
                        content={`${selectedUser!.companies ? selectedUser!.companies.split(";").join("\n") : "None"}`}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                          property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                          content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
                        }}
                        actionButton={true}
                        actionButtonsList={[
                          {
                            tooltip: "Edit companies",
                            disabled: false,
                            onClick: () => {
                              dispatchDialogState({
                                dialogActionButtonLabel: "Save",
                                title: "Choose companies",
                                description: "Choose companies to be managed by this user.",
                                property: "Companies",
                                inputType: "multipleSelect",
                                saveButtonLoading: false,
                                socketSignal: 'set_companies',
                                separator: ';',
                                itemsList: [], // this is already preloaded to improve performance
                                itemValue: selectedUser!.companies,
                                itemIsValid:  true
                              })
                              setDialogOpen(true)
                            },
                            icon: <EditRounded />
                          }
                        ]}
                      />
                      <PropertyArea
                        key={1}
                        property={"Embossers"}
                        content={`${selectedUser!.role.includes("Administrator") ? "All" : selectedUser!.serialNumbers ? selectedUser!.serialNumbers.split(",").join(", ") : "None"}`}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                          content: `${classes['propertyAreaContent']} ${classes['']}`
                        }}
                        actionButton={true}
                        actionButtonsList={[
                          {
                            tooltip: "Edit embossers",
                            disabled: false,
                            onClick: () => {
                              dispatchDialogState({
                                dialogActionButtonLabel: "Save",
                                title: "Choose embossers",
                                description: "Choose embossers to be managed by this user.",
                                property: "Embossers",
                                inputType: "textField",
                                inputLabel: "Serial Numbers",
                                inputRegex: "^([0-9]{5},)*[0-9]{5}$",
                                saveButtonLoading: false,
                                socketSignal: 'set_serial_numbers',
                                itemValue: selectedUser!.serialNumbers,
                                itemIsValid: true
                              })
                              setDialogOpen(true)
                            },
                            icon: <EditRounded />
                          }
                        ]}
                      />
                      <PropertyArea
                        key={2}
                        property={"Manufacturers"}
                        content={`${selectedUser!.role.includes("Administrator") ? "All" : selectedUser!.manufacturer ? selectedUser!.manufacturer.split(",").join(", ") : "All"}`}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                          property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                          content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
                        }}
                        actionButton={true}
                        actionButtonsList={[
                          {
                            tooltip: "Edit manufacturers",
                            disabled: false,
                            onClick: () => {
                              dispatchDialogState({
                                dialogActionButtonLabel: "Save",
                                title: "Choose manufacturers",
                                description: "Choose manufacturers to be displayed to this user.",
                                property: "Manufacturers",
                                inputType: "multipleSelect",
                                saveButtonLoading: false,
                                socketSignal: 'set_manufacturer',
                                separator: ',',
                                itemsList: manufacturersList,
                                itemValue: selectedUser!.manufacturer,
                                itemIsValid:  true
                              })
                              setDialogOpen(true)
                            },
                            icon: <EditRounded />
                          }
                        ]}
                      />
                    </>
                  }
                />
            }
            {
              (currentRole === "Global Administrator" && selectedUser!.role.includes("Translator")) &&
                <MultiplePropertiesArea
                  topMargin={true}
                  property={"Translation"}
                  classes={classes}
                  children={
                    <>
                      <PropertyArea
                        key={3}
                        property={"Speech Translation Files"}
                        content={`${selectedUser!.availableSpeechTranslationFiles ? selectedUser!.availableSpeechTranslationFiles.split(";").join("\n") : "None"}`}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                          property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                          content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
                        }}
                        actionButton={true}
                        actionButtonsList={[
                          {
                            tooltip: "Edit speech translation files",
                            disabled: false,
                            onClick: () => {
                              dispatchDialogState({
                                dialogActionButtonLabel: "Save",
                                title: "Choose speech translation files",
                                description: "Choose speech translation files to be managed by this user.",
                                property: "Speech",
                                inputType: "multipleSelect",
                                saveButtonLoading: false,
                                socketSignal: 'set_available_speech_translation_files',
                                separator: ';',
                                itemsList: [],
                                itemValue: selectedUser!.availableSpeechTranslationFiles,
                                itemIsValid:  true
                              })
                              setDialogOpen(true)
                            },
                            icon: <EditRounded />
                          }
                        ]}
                      />
                      <PropertyArea
                        key={4}
                        property={"Web Interface Translation Files"}
                        content={`${selectedUser!.availableWebInterfaceTranslationFiles ? selectedUser!.availableWebInterfaceTranslationFiles.split(";").join("\n") : "None"}`}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                          content: `${classes['propertyAreaContent']} ${classes['']}`
                        }}
                        actionButton={true}
                        actionButtonsList={[
                          {
                            tooltip: "Edit web interface translation files",
                            disabled: false,
                            onClick: () => {
                              dispatchDialogState({
                                dialogActionButtonLabel: "Save",
                                title: "Choose web interface translation files",
                                description: "Choose web interface translation files to be managed by this user.",
                                property: "WebInterface",
                                inputType: "multipleSelect",
                                saveButtonLoading: false,
                                socketSignal: 'set_available_web_interface_translation_files',
                                separator: ';',
                                itemsList: [],
                                itemValue: selectedUser!.availableWebInterfaceTranslationFiles,
                                itemIsValid:  true
                              })
                              setDialogOpen(true)
                            },
                            icon: <EditRounded />
                          }
                        ]}
                      />
                      <PropertyArea
                          key={5}
                          property={"IBTG Translation Files"}
                          content={`${selectedUser!.availableIBTGTranslationFiles ? selectedUser!.availableIBTGTranslationFiles.split(";").join("\n") : "None"}`}
                          classes={{
                            container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                            property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                            content: `${classes['propertyAreaContent']} ${classes['']}`
                          }}
                          actionButton={true}
                          actionButtonsList={[
                            {
                              tooltip: "Edit IBTG translation files",
                              disabled: false,
                              onClick: () => {
                                dispatchDialogState({
                                  dialogActionButtonLabel: "Save",
                                  title: "Choose IBTG translation files",
                                  description: "Choose IBTG translation files to be managed by this user.",
                                  property: "IBTG",
                                  inputType: "multipleSelect",
                                  saveButtonLoading: false,
                                  socketSignal: 'set_available_ibtg_translation_files',
                                  separator: ';',
                                  itemsList: IBTGTranslationFiles,
                                  itemValue: selectedUser!.availableIBTGTranslationFiles,
                                  itemIsValid:  true
                                })
                                setDialogOpen(true)
                              },
                              icon: <EditRounded />
                            }
                          ]}
                        />
                    </>
                  }
                />
            }
            <PropertyArea
              property={"Role"}
              content={`${selectedUser!.role !== "" ? selectedUser!.role.split(';').join('\n') : "\n"}`}
              classes={{
                container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
              }}
              actionButton={true}
              actionButtonsList={[
                {
                  tooltip: "Edit role",
                  disabled: (selectedUser!.role.includes("Administrator") && !(currentRole === "Global Administrator")) || selectedUser!.id === currentUser!.uid,
                  onClick: () => {
                    dispatchDialogState({
                      dialogActionButtonLabel: "Save",
                      title: "Choose user's role",
                      description: "Choose a role for this user.",
                      property: "Role",
                      inputType: "multipleSelect",
                      saveButtonLoading: false,
                      socketSignal: 'set_role',
                      separator: ';',
                      itemsList: rolesList,
                      itemValue: selectedUser!.role,
                      itemIsValid:  true
                    })
                    setDialogOpen(true)
                  },
                  icon: <EditRounded />
                }
              ]}
            />
            <PropertyArea
              property={"Document ID"}
              content={selectedUser!.id !== '' ? selectedUser!.id : "Unknown"}
              classes={{
                container: `${classes['propertyArea']} ${classes['propertyAreaSmallTopMargin']}`,
                property: `${classes['propertyAreaProperty']} ${classes['noOverflow']}`,
                content: `${classes['propertyAreaContent']} ${classes['noOverflow']}`
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              height: "2.5em",
              mt: 2,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <IconButton
              onClick={prevUser}
              sx={{
                color: mode === "light" ? "rgba(0, 0, 0, 0.54)": "white",
                width: "2em",
                height: "2em",
                borderRadius: "50%",
              }}
            >
              <ArrowBackRounded fontSize="large"/>
            </IconButton>
            <IconButton
              onClick={nextUser}
              sx={{
                color: mode === "light" ? "rgba(0, 0, 0, 0.54)": "white",
                width: "2em",
                height: "2em",
                borderRadius: "50%",
              }}
            >
              <ArrowForwardRounded fontSize="large"/>
            </IconButton>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={dialogOpen}
        PaperProps={{
          style: {
            backgroundColor: mode === 'light' ? "#f6f6f6" : "#151515",
            borderRadius: "10px",
            boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)"
          }
        }}
        keepMounted={false}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogState.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogState.description}
          </DialogContentText>
          {dialogInput}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false)
            }}
            color="error"
          >
            Cancel
          </Button>
          <LoadingButton
            loading={dialogState.saveButtonLoading}
            onClick={async () => {
              dispatchDialogState({saveButtonLoading: true})
              if (dialogState.property === "Role") {
                const changeRole = httpsCallable(functions, 'changeRole')
                await changeRole({ email: selectedUser!.email, role: dialogState.itemValue })
              }
              socket.emit(`${dialogState.socketSignal}`, selectedUser!.id, dialogState.itemValue)
              dispatchDialogState({saveButtonLoading: false})
              setDialogOpen(false)
            }}
            autoFocus
            color="secondary"
            disabled={!dialogState.itemIsValid}
          >
            {dialogState.dialogActionButtonLabel}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}